import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import { validateAttachments, generateStorageFileName } from '../utils/attachmentValidation';

/**
 * Service for handling user feedback submissions and metadata collection
 */
class FeedbackService {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.pageHistory = [];
    this.pageStartTime = Date.now();
    
    // Track page navigation
    this.trackPageNavigation();
  }

  /**
   * Generate a unique session ID
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Track page navigation for session history
   */
  trackPageNavigation() {
    // Track initial page
    this.pageHistory.push({
      url: window.location.href,
      pathname: window.location.pathname,
      timestamp: Date.now()
    });

    // Listen for navigation changes (for SPAs)
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = (...args) => {
      originalPushState.apply(window.history, args);
      this.trackPageChange();
    };

    window.history.replaceState = (...args) => {
      originalReplaceState.apply(window.history, args);
      this.trackPageChange();
    };

    // Listen for popstate (back/forward buttons)
    window.addEventListener('popstate', () => {
      this.trackPageChange();
    });
  }

  /**
   * Track page changes
   */
  trackPageChange() {
    this.pageHistory.push({
      url: window.location.href,
      pathname: window.location.pathname,
      timestamp: Date.now()
    });

    // Keep only last 10 pages to avoid memory issues
    if (this.pageHistory.length > 10) {
      this.pageHistory = this.pageHistory.slice(-10);
    }

    // Reset page start time
    this.pageStartTime = Date.now();
  }

  /**
   * Get device type based on screen size and user agent
   */
  getDeviceType() {
    const width = window.innerWidth;
    const userAgent = navigator.userAgent.toLowerCase();

    if (width <= 768) {
      return 'mobile';
    } else if (width <= 1024) {
      return 'tablet';
    } else {
      return 'desktop';
    }
  }

  /**
   * Get browser information from user agent
   */
  getBrowserInfo() {
    const userAgent = navigator.userAgent;
    
    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
      return 'Chrome';
    } else if (userAgent.includes('Firefox')) {
      return 'Firefox';
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      return 'Safari';
    } else if (userAgent.includes('Edg')) {
      return 'Edge';
    } else if (userAgent.includes('Opera') || userAgent.includes('OPR')) {
      return 'Opera';
    } else {
      return 'Unknown';
    }
  }

  /**
   * Get operating system information
   */
  getOSInfo() {
    const userAgent = navigator.userAgent;
    
    if (userAgent.includes('Windows')) {
      return 'Windows';
    } else if (userAgent.includes('Mac OS')) {
      return 'macOS';
    } else if (userAgent.includes('Linux')) {
      return 'Linux';
    } else if (userAgent.includes('Android')) {
      return 'Android';
    } else if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) {
      return 'iOS';
    } else {
      return 'Unknown';
    }
  }

  /**
   * Calculate time spent on current page
   */
  getTimeOnPage() {
    return Math.floor((Date.now() - this.pageStartTime) / 1000);
  }

  /**
   * Get previous pages from history (excluding current page)
   */
  getPreviousPages() {
    return this.pageHistory
      .slice(0, -1) // Exclude current page
      .map(page => page.pathname)
      .slice(-5); // Last 5 pages
  }

  /**
   * Extract user information from auth context
   */
  extractUserInfo(user, userProfile) {
    if (!user) {
      return {
        id: null,
        email: null,
        name: null
      };
    }

    // Try to get name from multiple sources
    let userName = null;
    
    if (userProfile?.basicInfo?.firstName && userProfile?.basicInfo?.lastName) {
      userName = `${userProfile.basicInfo.firstName} ${userProfile.basicInfo.lastName}`;
    } else if (userProfile?.basicInfo?.firstName) {
      userName = userProfile.basicInfo.firstName;
    } else if (userProfile?.basicInfo?.name) {
      userName = userProfile.basicInfo.name;
    } else if (userProfile?.displayName) {
      userName = userProfile.displayName;
    } else if (userProfile?.firstName && userProfile?.lastName) {
      userName = `${userProfile.firstName} ${userProfile.lastName}`;
    } else if (userProfile?.firstName) {
      userName = userProfile.firstName;
    } else if (user.displayName) {
      userName = user.displayName;
    }

    return {
      id: user.uid,
      email: user.email || userProfile?.email || userProfile?.basicInfo?.email,
      name: userName
    };
  }

  /**
   * Collect comprehensive metadata about the user's context
   */
  collectMetadata(user = null, userProfile = null) {
    const now = new Date();
    
    return {
      // User Information
      user: this.extractUserInfo(user, userProfile),
      
      // Context Information
      context: {
        url: window.location.href,
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        referrer: document.referrer || 'Direct',
        timestamp: now.toISOString(),
        title: document.title
      },
      
      // Technical Information
      technical: {
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        screen: {
          width: window.screen.width,
          height: window.screen.height,
          availWidth: window.screen.availWidth,
          availHeight: window.screen.availHeight,
          colorDepth: window.screen.colorDepth,
          pixelDepth: window.screen.pixelDepth
        },
        deviceType: this.getDeviceType(),
        browser: this.getBrowserInfo(),
        os: this.getOSInfo(),
        language: navigator.language,
        languages: navigator.languages,
        platform: navigator.platform,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timezoneOffset: now.getTimezoneOffset()
      },
      
      // Session Information
      session: {
        sessionId: this.sessionId,
        timeOnPage: this.getTimeOnPage(),
        previousPages: this.getPreviousPages(),
        pageHistory: this.pageHistory.slice(-5), // Last 5 pages with timestamps
        sessionStartTime: this.pageHistory[0]?.timestamp || Date.now()
      },

      // Performance Information (if available)
      performance: this.getPerformanceInfo()
    };
  }

  /**
   * Get performance information if available
   */
  getPerformanceInfo() {
    if (!window.performance) {
      return null;
    }

    try {
      const navigation = performance.getEntriesByType('navigation')[0];
      const paint = performance.getEntriesByType('paint');
      
      return {
        loadTime: navigation ? Math.round(navigation.loadEventEnd - navigation.fetchStart) : null,
        domContentLoaded: navigation ? Math.round(navigation.domContentLoadedEventEnd - navigation.fetchStart) : null,
        firstPaint: paint.find(p => p.name === 'first-paint')?.startTime || null,
        firstContentfulPaint: paint.find(p => p.name === 'first-contentful-paint')?.startTime || null,
        memoryUsage: performance.memory ? {
          used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024), // MB
          total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024), // MB
          limit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024) // MB
        } : null
      };
    } catch (error) {
      console.warn('Could not collect performance info:', error);
      return null;
    }
  }

  /**
   * Upload attachments to Firebase Storage
   */
  async uploadAttachments(files, feedbackId) {
    if (!files || files.length === 0) {
      return [];
    }

    // Validate files before upload
    const validation = validateAttachments(files);
    if (!validation.isValid) {
      throw new Error(`File validation failed: ${validation.errors.join(', ')}`);
    }

    const uploadPromises = files.map(async (file, index) => {
      try {
        const fileName = generateStorageFileName(file.name, feedbackId, index);
        const storageRef = ref(storage, fileName);
        
        // Upload file
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        
        return {
          originalName: file.name,
          fileName,
          downloadURL,
          size: file.size,
          type: file.type,
          uploadedAt: new Date().toISOString()
        };
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        throw new Error(`Failed to upload ${file.name}: ${error.message}`);
      }
    });

    try {
      const uploadResults = await Promise.all(uploadPromises);
      console.log(`Successfully uploaded ${uploadResults.length} files`);
      return uploadResults;
    } catch (error) {
      console.error('Error uploading attachments:', error);
      throw error;
    }
  }

  /**
   * Submit feedback to Firestore
   */
  async submitFeedback(feedbackData, user = null, userProfile = null) {
    try {
      const metadata = this.collectMetadata(user, userProfile);
      
      // Generate a unique feedback ID for file organization
      const tempFeedbackId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const submission = {
        type: feedbackData.type,
        title: feedbackData.title.trim(),
        userId: metadata.user.id,
        userEmail: metadata.user.email,
        createdAt: serverTimestamp(),
        metadata,
        timestamp: serverTimestamp(),
        status: 'submitted'
      };

      // Add description only if provided
      if (feedbackData.description && feedbackData.description.trim()) {
        submission.description = feedbackData.description.trim();
      }

      // Handle file attachments if present
      let attachmentInfo = null;
      if (feedbackData.attachments && feedbackData.attachments.length > 0) {
        try {
          console.log(`Uploading ${feedbackData.attachments.length} attachments...`);
          const uploadedFiles = await this.uploadAttachments(feedbackData.attachments, tempFeedbackId);
          
          attachmentInfo = {
            count: uploadedFiles.length,
            files: uploadedFiles,
            totalSize: uploadedFiles.reduce((sum, file) => sum + file.size, 0)
          };
          
          submission.attachments = attachmentInfo;
          console.log('Attachments uploaded successfully');
        } catch (uploadError) {
          console.error('Attachment upload failed:', uploadError);
          // Continue with submission but note the attachment failure
          submission.attachmentError = uploadError.message;
          submission.attachmentCount = feedbackData.attachments.length;
        }
      }

      // Add to Firestore collection
      const docRef = await addDoc(collection(db, 'user_feedback'), submission);
      
      console.log('Feedback submitted successfully:', docRef.id);
      
      return {
        success: true,
        id: docRef.id,
        attachments: attachmentInfo
      };
      
    } catch (error) {
      console.error('Error submitting feedback:', error);
      
      // Try to save to localStorage as backup (without files for storage reasons)
      this.saveToLocalStorage(feedbackData, user, userProfile);
      
      throw new Error(`Failed to submit feedback: ${error.message}`);
    }
  }

  /**
   * Save feedback to localStorage as backup
   */
  saveToLocalStorage(feedbackData, user, userProfile) {
    try {
      const metadata = this.collectMetadata(user, userProfile);
      const backup = {
        type: feedbackData.type,
        title: feedbackData.title,
        userId: metadata.user.id,
        userEmail: metadata.user.email,
        createdAt: new Date().toISOString(),
        metadata,
        timestamp: new Date().toISOString(),
        status: 'pending_retry'
      };

      // Add description only if provided
      if (feedbackData.description && feedbackData.description.trim()) {
        backup.description = feedbackData.description;
      }

      // Note attachment count but don't store actual files
      if (feedbackData.attachments && feedbackData.attachments.length > 0) {
        backup.attachmentCount = feedbackData.attachments.length;
        backup.attachmentNote = 'Attachments were not saved in backup - please reattach files when retrying';
      }

      const existingBackups = JSON.parse(localStorage.getItem('feedback_backups') || '[]');
      existingBackups.push(backup);
      
      // Keep only last 10 backups
      if (existingBackups.length > 10) {
        existingBackups.splice(0, existingBackups.length - 10);
      }
      
      localStorage.setItem('feedback_backups', JSON.stringify(existingBackups));
      console.log('Feedback saved to localStorage as backup');
      
    } catch (error) {
      console.error('Could not save feedback backup to localStorage:', error);
    }
  }

  /**
   * Retry failed submissions from localStorage
   */
  async retryFailedSubmissions() {
    try {
      const backups = JSON.parse(localStorage.getItem('feedback_backups') || '[]');
      
      if (backups.length === 0) {
        return { success: true, retriedCount: 0 };
      }

      let successCount = 0;
      const remainingBackups = [];

      for (const backup of backups) {
        try {
          const retrySubmission = {
            type: backup.type,
            title: backup.title,
            userId: backup.userId,
            userEmail: backup.userEmail,
            createdAt: serverTimestamp(),
            metadata: backup.metadata,
            timestamp: serverTimestamp(),
            status: 'submitted_retry'
          };

          // Add description only if it exists in backup
          if (backup.description) {
            retrySubmission.description = backup.description;
          }

          const docRef = await addDoc(collection(db, 'user_feedback'), retrySubmission);
          
          console.log('Backup feedback submitted successfully:', docRef.id);
          successCount++;
          
        } catch (error) {
          console.error('Failed to retry backup submission:', error);
          remainingBackups.push(backup);
        }
      }

      // Update localStorage with remaining failed submissions
      localStorage.setItem('feedback_backups', JSON.stringify(remainingBackups));
      
      return {
        success: true,
        retriedCount: successCount,
        remainingCount: remainingBackups.length
      };
      
    } catch (error) {
      console.error('Error retrying failed submissions:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get count of pending backup submissions
   */
  getPendingBackupCount() {
    try {
      const backups = JSON.parse(localStorage.getItem('feedback_backups') || '[]');
      return backups.length;
    } catch (error) {
      console.error('Error getting backup count:', error);
      return 0;
    }
  }
}

// Create singleton instance
const feedbackService = new FeedbackService();

export default feedbackService;